import type { AnnotationEntry } from '@geosoft/viewer-sdk';
import { usePluginStore } from '@/state/usePluginStore';
import { summarizeMeasurements } from './measurementPlugin';

interface MeasurementPanelProps {
  readonly selectedPoint: readonly [number, number] | null;
  readonly measurePoints: ReadonlyArray<readonly [number, number]>;
  readonly annotations: ReadonlyArray<AnnotationEntry>;
  readonly onAddPoint: () => void;
  readonly onReset: () => void;
}

export function MeasurementPanel({
  measurePoints,
  annotations,
  onReset,
}: MeasurementPanelProps) {
  const { engineType, engineReady, isPluginAvailable, getPluginState, transient } = usePluginStore();
  const available = isPluginAvailable('measurement');
  const state = getPluginState('measurement');
  const summary = summarizeMeasurements(measurePoints, annotations);

  if (!available) {
    return (
      <div className="plugin-not-available">
        <div className="plugin-status-banner">
          <span className="material-symbols-outlined">warning</span>
          <div>
            <strong>Measurement not available</strong>
            <p>
              {!engineType && 'Map engine not initialized'}
              {engineType && !engineReady && `${engineType} engine loading...`}
              {engineType && engineReady && `${engineType} engine does not support measurement`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'backend-not-ready') {
    return (
      <div className="plugin-backend-not-ready">
        <div className="plugin-status-banner">
          <span className="material-symbols-outlined">cloud_off</span>
          <div>
            <strong>Backend not ready</strong>
            <p>Measurement service is not available. Using local calculation only.</p>
          </div>
        </div>
        {/* Show basic measurement functionality */}
      </div>
    );
  }

  return (
    <>
      <div className="plugin-engine-info">
        <span className="engine-badge">
          <span className="material-symbols-outlined">
            {engineType === 'maplibre' ? 'map' : engineType === 'cesium' ? 'language' : 'layers'}
          </span>
          {engineType} engine
        </span>
        {state === 'computing' && (
          <span className="computing-indicator">
            <span className="material-symbols-outlined">hourglass_empty</span>
            Computing...
          </span>
        )}
      </div>

      <p>
        {measurePoints.length === 0
          ? 'Click on the map to place points. Distance is calculated automatically.'
          : measurePoints.length === 1
            ? `1 point placed. Click the map to add a second point for distance.`
            : `${measurePoints.length} points placed.`}
      </p>

      <div className="plugin-actions">
        <button 
          type="button" 
          onClick={onReset}
          disabled={state === 'computing' || measurePoints.length === 0}
        >
          <span className="material-symbols-outlined">refresh</span>
          Clear all points
        </button>
      </div>

      <dl className="plugin-measurements" aria-label="Measurement results">
        <div>
          <dt>
            <span className="material-symbols-outlined">straighten</span>
            Distance
          </dt>
          <dd className="measurement-value">{summary.distanceLabel}</dd>
        </div>
        <div>
          <dt>
            <span className="material-symbols-outlined">polyline</span>
            Surface
          </dt>
          <dd className="measurement-value">{summary.surfaceLabel}</dd>
        </div>
        <div>
          <dt>
            <span className="material-symbols-outlined">architecture</span>
            Angle
          </dt>
          <dd className="measurement-value">{summary.angleLabel}</dd>
        </div>
        <div>
          <dt>
            <span className="material-symbols-outlined">height</span>
            Height
          </dt>
          <dd className="measurement-value">{summary.heightLabel}</dd>
        </div>
      </dl>

      {transient.measurement.vertices.length > 0 && (
        <div className="plugin-save-actions">
          <button 
            type="button"
            className="primary"
            disabled={state === 'computing'}
          >
            <span className="material-symbols-outlined">check_circle</span>
            Save measurement
          </button>
        </div>
      )}
    </>
  );
}
